import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './UserAccountPage.css';
import Preloader from '../common/preloader';

const UserAccountPage = () => {
  const [user, setUser] = useState({ name: '', addresses: [] });
  const [newAddress, setNewAddress] = useState({
    name: '',
    locality: '',
    subLocality: '',
    district: '',
    pincode: '',
    phone: '',
  });
  const [editAddress, setEditAddress] = useState(null);
  const [errors, setErrors] = useState({});
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    window.scrollTo(0, 0);
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser({ ...response.data, addresses: response.data.addresses || [] });
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        }
        setErrors({ form: err.response?.data?.message || 'Failed to load profile.' });
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate, API_BASE_URL]);

  const validateAddress = (address) => {
    const newErrors = {};
    if (!address.name.trim()) newErrors.name = 'Name is required';
    if (!address.locality.trim()) newErrors.locality = 'Locality is required';
    if (!address.district.trim()) newErrors.district = 'District is required';
    if (!address.pincode.match(/^\d{6}$/)) newErrors.pincode = 'Pincode must be 6 digits';
    if (!address.phone.match(/^\d{10}$/)) newErrors.phone = 'Phone must be 10 digits';
    return newErrors;
  };

  const handleAddressChange = (e) => {
    setNewAddress({ ...newAddress, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleEditChange = (e) => {
    setEditAddress({ ...editAddress, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    const validationErrors = validateAddress(newAddress);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/auth/add-address`,
        newAddress,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setUser({ ...response.data, addresses: response.data.addresses || [] });
      setNewAddress({ name: '', locality: '', subLocality: '', district: '', pincode: '', phone: '' });
      setShowAddPopup(false);
    } catch (err) {
      setErrors({ form: err.response?.data?.message || 'Failed to add address.' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAddress = async (e) => {
    e.preventDefault();
    const validationErrors = validateAddress(editAddress);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/auth/update-address/${editAddress._id}`,
        editAddress,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setUser({ ...response.data, addresses: response.data.addresses || [] });
      setEditAddress(null);
    } catch (err) {
      setErrors({ form: err.response?.data?.message || 'Failed to update address.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/auth/delete-address/${addressId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setUser({ ...response.data, addresses: response.data.addresses || [] });
      setShowDeletePopup(null);
    } catch (err) {
      setErrors({ form: err.response?.data?.message || 'Failed to delete address.' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  if (loading) {
    return <Preloader />;
  }

  return (
    <>
      <Helmet>
        <title>Your Account | binzal - Manage Your Profile</title>
        <meta
          name="description"
          content="Manage your account details and addresses on binzal – Bihar's trusted grocery delivery service."
        />
        <meta
          name="keywords"
          content="binzal account, manage addresses, user profile, grocery delivery Bihar"
        />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content="Your Account | binzal" />
        <meta
          property="og:description"
          content="Update your profile and addresses for seamless grocery delivery with binzal in Bihar."
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      <div className="ua-container">
        <h2 className="ua-heading mt-2">Hello, {user.name}!</h2>
        {errors.form && <div className="ua-error">{errors.form}</div>}

        <div className="ua-section">
          <h4 className="ua-section-heading">Your Addresses</h4>
          {!user.addresses || user.addresses.length === 0 ? (
            <p className="ua-address-details">No addresses found.</p>
          ) : (
            user.addresses.map((address) => (
              <div key={address._id} className="ua-address">
                <button
                  className="ua-btn edit-btn"
                  onClick={() => setEditAddress(address)}
                  disabled={loading}
                >
                  <i className="bi bi-pencil"></i>
                  <span>Edit</span>
                </button>
                <p className="ua-address-details">
                  <strong className='text-success'>{address.name}</strong><br />
                  <span className="ua-address-key">Locality:</span> {address.locality}<br />
                  {address.subLocality && (
                    <>
                      <span className="ua-address-key">Road:</span> {address.subLocality}<br />
                    </>
                  )}
                  <span className="ua-address-key">District:</span> {address.district}<br />
                  <span className="ua-address-key">Pincode:</span> {address.pincode}<br />
                  <span className="ua-address-key">Phone:</span> {address.phone}
                </p>
                <button
                  className="ua-btn ua-btn-secondary"
                  onClick={() => setShowDeletePopup(address._id)}
                  disabled={loading}
                >
                  Delete
                </button>
              </div>
            ))
          )}
          <div className="ua-btn-group">
            <button
              className="ua-btn ua-btn-primary"
              onClick={() => setShowAddPopup(true)}
              disabled={loading}
            >
              Add Address
            </button>
            <button
              className="ua-btn btn-logout"
              onClick={handleLogout}
              disabled={loading}
            >
              Logout <i className="bi bi-box-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>

      {(showAddPopup || showDeletePopup || editAddress) && (
        <div className="ua-popup-overlay">
          {showAddPopup && (
            <PopupForm
              heading="Add New Address"
              address={newAddress}
              handleChange={handleAddressChange}
              handleSubmit={handleAddAddress}
              setClose={() => setShowAddPopup(false)}
              errors={errors}
              loading={loading}
            />
          )}

          {editAddress && (
            <PopupForm
              heading="Edit Address"
              address={editAddress}
              handleChange={handleEditChange}
              handleSubmit={handleUpdateAddress}
              setClose={() => setEditAddress(null)}
              errors={errors}
              loading={loading}
            />
          )}

          {showDeletePopup && (
            <div className="ua-popup" onClick={(e) => e.stopPropagation()}>
              <h4 className="ua-popup-heading">Confirm Deletion</h4>
              <p>Are you sure you want to delete this address?</p>
              <div className="ua-action-buttons">
                <button
                  type="button"
                  className="ua-btn ua-btn-danger"
                  onClick={() => handleDeleteAddress(showDeletePopup)}
                  disabled={loading}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="ua-btn ua-btn-secondary"
                  onClick={() => setShowDeletePopup(null)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

const PopupForm = ({ heading, address, handleChange, handleSubmit, setClose, errors, loading }) => (
  <div className="ua-popup" onClick={(e) => e.stopPropagation()}>
    <h4 className="ua-popup-heading">{heading}</h4>
    <form onSubmit={handleSubmit}>
      {["name", "locality", "subLocality", "district", "pincode", "phone"].map((field) => (
        <div className="ua-form-group" key={field}>
          <label className="ua-label">
            {field === "subLocality" ? "Sub-Locality/Road" :
              field === "phone" ? "Phone Number" : field.charAt(0).toUpperCase() + field.slice(1)}
          </label>
          <input
            type="text"
            name={field}
            className={`ua-input ${errors[field] ? 'ua-invalid' : ''}`}
            value={address[field]}
            onChange={handleChange}
            disabled={loading}
            required={["name", "locality", "district", "pincode", "phone"].includes(field)}
          />
          {errors[field] && <div className="ua-error-feedback">{errors[field]}</div>}
        </div>
      ))}
      <div className="ua-action-buttons">
        <button type="submit" className="ua-btn ua-btn-primary" disabled={loading}>
          Save
        </button>
        <button
          type="button"
          className="ua-btn ua-btn-secondary"
          onClick={setClose}
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    </form>
  </div>
);

export default UserAccountPage;